import React from "react";

import classnames from "classnames";
import PropTypes from "prop-types";
import { withStyleProps } from "Utils/styleProps";

type BodyTextSize = "caption" | "subBody" | "body2" | "body1" | "large";
type BodyTextWeight = "bold" | "normal" | "light";
type BodyTextColor = "error" | "warning" | "success" | "muted" | "white";

interface BodyTextProps {
  size?: BodyTextSize;
  componentClass?: React.ElementType;
  weight?: BodyTextWeight;
  underlined?: boolean;
  color?: BodyTextColor;
  children?: React.ReactNode;
  className?: string;
}

const BodyTextBase = React.forwardRef<HTMLElement, BodyTextProps & React.HTMLAttributes<HTMLElement>>(
  ({ size = "body1", componentClass = "div", weight, underlined, color, children, className, ...rest }, ref) => {
    const classes = classnames(
      "BodyTextCobalt",
      `BodyTextCobalt--${size}`,
      {
        [`BodyTextCobalt--${weight}`]: weight,
        "BodyTextCobalt--underlined": underlined,
        [`BodyTextCobalt--${color}`]: color,
      },
      className,
    );
    return React.createElement(
      componentClass,
      {
        className: classes,
        ref,
        ...rest,
      },
      children,
    );
  },
);

BodyTextBase.displayName = "BodyText";

// @ts-expect-error - propTypes for legacy compatibility
BodyTextBase.propTypes = {
  size: PropTypes.oneOf(["caption", "subBody", "body2", "body1", "large"]),
  weight: PropTypes.oneOf(["bold", "normal", "light"]),
  underlined: PropTypes.bool,
  color: PropTypes.oneOf(["error", "warning", "success", "muted", "white"]),
};

// Export size-specific variants using withStyleProps defaultProps
export const Caption = withStyleProps(BodyTextBase, { size: "caption" });
export const SubBody = withStyleProps(BodyTextBase, { size: "subBody" });
export const Body2 = withStyleProps(BodyTextBase, { size: "body2" });
export const Body1 = withStyleProps(BodyTextBase, { size: "body1" });

export const BodyLarge = withStyleProps(BodyTextBase, { size: "large" });
export const BodyMedium = withStyleProps(BodyTextBase, { size: "body1" });
export const BodySmall = withStyleProps(BodyTextBase, { size: "body2" });

export const LabelMedium = withStyleProps(BodyTextBase, { size: "body2" });
export const LabelSmall = withStyleProps(BodyTextBase, { size: "caption", weight: "bold", color: "muted" });

const BodyText = withStyleProps(BodyTextBase);
export default BodyText;
